// Forked from types/case/models.ts at `cases-pre-sr-fork` (Service Requests start as a copy of Cases).
// Comments below were written for Cases and still describe this file until the two diverge.
import type { TContact } from '../contact/models'
import type { IServiceRequestNote } from '../note/models'
import type { IServiceRequestDocument } from '../document/models'
import type { ICompanyMSService } from '../company/models'
import type { IEmailMessage } from '../email/models'
import type {
  IEntityDataItem,
  ISelectItem,
  IEntitySelectItem,
  TDisplayString,
  TEntityId,
  INonEntityForPost,
} from '~/types/models'

/**
 * How a case came to exist (issue #1635).
 *
 * An enum rather than a `createdFromEmail` boolean, mirroring the server's
 * `ServiceRequestSource`: the ingest pipeline is already written for a second channel (a
 * RingCentral path would enqueue the same task type), which becomes a member
 * here rather than another flag.
 *
 * ⚠ The VALUES are the wire format and must match the server's literals byte
 * for byte. `source` arrives as a plain JSON string and httpGet CASTS rather
 * than validates, so a renamed value would not throw -- every comparison would
 * just quietly go false.
 *
 * ⚠ The first enum in `types/`, which is otherwise type-only. This module now
 * emits runtime JS, so reach for the members with a VALUE import
 * (`import { ServiceRequestSource }`), not `import type`.
 */
export enum ServiceRequestSource {
  MANUAL = 'manual',
  EMAIL = 'email',
}

export interface IServiceRequest extends IEntityDataItem {
  descr: string
  // One datetime, the contract Cases moved to in WXTG/cynque-api-server#1723 /
  // cynque-webapp#147 -- adopted here from day one, before the Service Request
  // API existed.
  openedAt: string
  type: IEntitySelectItem | null
  numLocationsAffected: number | null
  contact: TContact | null
  companyMSService: ICompanyMSService | null
  supplier: IEntitySelectItem | null
  assignedTo: IEntitySelectItem | null
  status: ISelectItem
  // How the case came to exist (issue #1635). ⚠ SERVER-MAINTAINED, and not
  // writable from here: the column is `insert_column=False` with a `'manual'`
  // default and `ExcludeFromMergePatch`, so neither a POST nor a PATCH can set
  // it. Every case the api creates takes the default; the email-to-case
  // pathway stamps its own with a separate UPDATE. That is why there is no
  // input-field stanza for it in the case spec, and must stay that way --
  // sending it would be silently ignored rather than rejected.
  //
  // Required-and-present, not `?`: unlike the nullable fields above it always
  // has a value, so a list read never drops the key.
  source: ServiceRequestSource
  // The case this one was MERGED INTO (cynque-api-server #1658), or null. A bare
  // id, not a select item. ⚠ SERVER-MAINTAINED like `source`: set only by
  // POST /api/servicerequests/{id}/merges, `insert_column=False` + `ExcludeFromMergePatch`,
  // and tied by a CHECK to status Merged -- so there is no input-field stanza
  // for it and it must never be sent. Optional because a payload from before
  // the merge feature carries no key.
  mergedInto?: TEntityId | null
  // ⚠ CLIENT-DERIVED, not a server property -- built by ServiceRequest() and optional for
  // that reason, where everything above is required-and-nullable because the
  // server demands it on a create. It never reaches a request body: only input
  // fields do, and there is no spec stanza for it.
  //
  // The supplier the case is about, from whichever anchor it has: its own
  // `supplier` when anchored to one, otherwise the SERVICE's supplier. Same
  // shape as the derived display props on IMSService.
  supplierDescr?: TDisplayString
  // ⚠ CLIENT-DERIVED too, and optional for the same reason as supplierDescr:
  // built by ServiceRequest(), never sent, no input-field stanza.
  //
  // The mdi name for the Service Requests list's icon column. That column carries the
  // header prop `showAsIcon`, which renders the cell VALUE as a <v-icon> -- so
  // the glyph NAME is the datum here, not a boolean.
  //
  // Set only for ServiceRequestSource.EMAIL. Left undefined for a manually-raised case,
  // which TableCard renders as an empty cell: `manual` is the overwhelming
  // majority and an icon on every row would say nothing.
  sourceIconName?: string
  // ⚠ CLIENT-DERIVED as well — built by ServiceRequest(), never sent.
  //
  // `source` rendered for people: 'Email' / 'Manual' rather than the wire
  // values 'email' / 'manual'. Exists because Group By renders the grouped
  // VALUE as the group's title verbatim, with no display mapping, so grouping
  // on `source` itself would head the groups in lower case.
  sourceDescr?: TDisplayString
}

export interface IServiceRequestType extends IEntityDataItem {
  descr: string
}

// ---------------------------------------------------------------------------
// Service Request overview — GET /api/servicerequests/{id}/overview. The response body is the
// usual envelope, `{ data: IServiceRequestOverview }`.
//
// Originally transcribed from the shapes proposed in WXTG/cynque-webapp#105,
// which derives them field by field from `ServiceRequestOverview` in the API server's
// resources/api/entities/models/case_overview.py. No longer a sketch: it is
// read by services/cases.ts getServiceRequestOverview, pages/Service Requests/Overview/[id].vue,
// the serviceRequestOverviewPage store and the MSW handler, so a change here moves all
// four. Check it against that model before adding to it.
//
// ⚠ IServiceRequestOverview is deliberately NARROWER than the payload. `ServiceRequestOverview`
// also sends `case_companies`, `case_projects`, `case_suppliers` and
// `case_tsds` — the annotation link collections — and they are left out
// here until something needs them. Reading is unaffected (extra keys are
// ignored), but do not read this type as proof the endpoint omits them.
//
// ⚠ Null semantics differ by ENDPOINT. The overview is built column by
// column by a view, so every key is always PRESENT — `null` or `[]` where
// empty. A list read (`GET /api/servicerequests`) instead runs optional-and-null
// fields through `remove_unset_ts_optionals`, which DROPS the key entirely.
// That is why IServiceRequest above spells its optional fields `?: T | null` while
// the overview spells its own `T | null`.
// ---------------------------------------------------------------------------

export interface IServiceRequestOverview {
  // Wider than what this endpoint actually sends: IServiceRequest is typed for the
  // list read, so its optional keys also admit `undefined`, which the
  // overview never emits.
  //
  // ⚠ THE CONTACT LIVES HERE, on the case, and there is no second copy.
  // This overview used to carry a standalone `contact` as well, because the
  // server built the two from different views -- `entities.case_overviews`
  // joined `entities.contacts` (the whole contact) while `entities._cases_build`
  // joined `select_entities.contacts` ({id, label}). `serviceRequest.contact` is now a
  // whole TContact on both reads, so the standalone key is gone.
  //
  // That split was not free while it lasted: ServiceRequestHeaderCard binds
  // `overviewData.serviceRequest` and renders `contact.descr`, while the page enriched
  // `overviewData.contact` -- a DIFFERENT object on the real API, and the same
  // one only under MSW, which returned a single shared reference. Enrich
  // `overviewData.serviceRequest.contact` and there is nothing left to get wrong.
  serviceRequest: IServiceRequest
  // IServiceRequestNote and IServiceRequestDocument live with the other fifteen of each, in
  // types/note/models.ts and types/document/models.ts -- they are ordinary
  // members of those families. Only IServiceRequestEmail is declared here, because it is
  // deliberately NOT an entity.
  serviceRequestNotes: IServiceRequestNote[]
  serviceRequestDocuments: IServiceRequestDocument[]
  serviceRequestEmails: IServiceRequestEmail[]
}

export type TEMailDirection = 'inbound' | 'outbound'

// A union rather than `string` on purpose: it makes "delivery failures must
// be visible" something vue-tsc enforces rather than a promise made in code
// review. A switch over it that forgets `bounced` will not compile.
export type TDeliveryState =
  | 'queued'
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'bounced'
  | 'spam_complaint'
  | 'failed'

// A message on a service request's thread: the generic IEmailMessage (types/
// email/models.ts), field for field -- the shape ICaseEmail has had since
// cynque-api-server #1737. Its own name because the Emails card and the
// overview read it as the service request's, and an SR-only field would land
// here.
//
// `body` is NULL on an auto-reply, a bounce notification, anything over the
// spam threshold -- and the overview returns those anyway, so the Emails card
// filters them out (buildServiceRequestEmailRows).
//
// ⚠ That text used to be a ServiceRequestNote the email pointed at
// (`serviceRequestNoteId`). It is `body` now, and `serviceRequestNotes` holds
// only notes.
export interface IServiceRequestEmail extends IEmailMessage {}

// The four annotation link entities, all the same shape. Each carries the
// LINK row, not the target it points at: `id` is the link's own id, and it
// is what you DELETE to detach. Each embeds the target's select item, so it
// still renders directly with no second call.
//
// Not referenced by IServiceRequestOverview above — see the note in its header. They
// are reached through their own endpoints: /api/servicerequestcompanies,
// /api/servicerequestprojects, /api/servicerequestsuppliers, /api/servicerequesttsds.
//
// ⚠ IServiceRequest.supplier and IServiceRequestSupplier are different things, and both exist:
// the field is the one supplier the case is ABOUT, the annotation list is
// every supplier it touches.

export interface IServiceRequestCompany extends IEntityDataItem {
  serviceRequest: IEntitySelectItem
  company: IEntitySelectItem
}

export interface IServiceRequestProject extends IEntityDataItem {
  serviceRequest: IEntitySelectItem
  project: IEntitySelectItem
}

export interface IServiceRequestSupplier extends IEntityDataItem {
  serviceRequest: IEntitySelectItem
  supplier: IEntitySelectItem
}

export interface IServiceRequestTsd extends IEntityDataItem {
  serviceRequest: IEntitySelectItem
  tsd: IEntitySelectItem
}

/**
 * The body of POST /api/servicerequests/{id}/replies -- a case's Send Email. The user
 * sets the recipients and subject (prefilled to answer the latest inbound
 * message); the threading headers that land it in the customer's existing
 * conversation stay server-side.
 *
 * cynque-api-server #1666 (PR #1672): `to` / `subject` omitted fall back to the
 * latest inbound correspondent and "Re: <descr>"; supplied, they are used as
 * sent, and `to` makes a case with no inbound email sendable. ⚠ The model is
 * `extra="forbid"`: ANY key not declared here is a 400, never ignored.
 * Validation (400): bare addresses only, none repeated across to/cc/bcc, at
 * most 50 recipients, a single-line subject -- mirrored client-side in
 * cynqueModules/emailRecipientRules.ts.
 *
 * ⚠ Since cynque-api-server #1674 (PR #1678) this is NOT the request body. The
 * endpoint is multipart-only (JSON is a 415): this object travels as the
 * `payload` part, as JSON, beside one `files` part per new attachment --
 * services/serviceRequestReply.ts builds that FormData.
 *
 * Extends INonEntityForPost: a reply is not an entity, so there is no id, hrid
 * or version to send.
 *
 * ⚠ Nothing is SENT by this request. It writes a `queued` message and returns
 * it; a worker does the sending. So a 201 means "recorded", not "delivered" --
 * watch IServiceRequestEmail.deliveryState for that.
 */
export interface IServiceRequestReplyForPost extends INonEntityForPost {
  note: string
  // Bare addresses (local@domain). Omitted -> whoever last wrote in.
  to?: string[]
  cc?: string[]
  bcc?: string[]
  // Sent verbatim. Omitted -> the server's "Re: <descr>" (or bare descr).
  subject?: string
  /**
   * Documents ALREADY on this case to send with the reply -- ids, not uploads.
   * Every id must belong to this case or the whole request is a 400. New files
   * go as the multipart `files` parts instead, and become case documents.
   */
  documentIds?: TEntityId[]
  /**
   * Send even though a recipient's address is suppressed. Not a bypass:
   * Postmark refuses suppressed addresses at their end, so this cannot make the
   * mail arrive. It exists so the refusal is a decision made knowingly.
   */
  sendToSuppressed?: boolean
  /**
   * The inbound email this answers -- an Emails row's Reply / Reply all
   * (cynque-api-server #1814, mirrored for Service Requests). It is marked
   * Replied and the reply threads onto it. Omitted (the '+' Send Email), the
   * latest inbound is. ⚠ Send it ONLY when that email carries `readStatus`.
   */
  inReplyToEmailId?: TEntityId
}
